import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

/* Bootstrap CSS + FontAwesome dropped in Phase 10. App.css now only
   carries legacy CSS-variable aliases so module CSS written against
   the qwen tokens keeps rendering in the new palette. */
import './App.css';

import AppLayout from './core/layout/AppLayout';
import Dashboard from './core/dashboard/Dashboard';
import Login from './core/auth/Login';
import Signup from './core/auth/Signup';
import PrivateRoute from './core/auth/PrivateRoute';
import Reports from './core/reports/Reports';
import ApiKey from './core/auth/ApiKey';
import OAuth2Redirect from './core/auth/OAuth2Redirect';
import LandingPage from './core/landing/LandingPage';
import PrivacyPolicy from './core/legal/PrivacyPolicy';
import ErrorBoundary from './core/common/ErrorBoundary';
import OnboardingModal, { hasSeenOnboarding } from './core/onboarding/OnboardingModal';
import { isAuthenticated, isAuthLoading, checkAuthStatus } from './core/api/auth';
import { getVersion } from './core/api/client';
import { TranslationProvider, useTranslation } from './core/context/TranslationContext';
import { ToastProvider } from './core/context/ToastContext';
import { ModulesProvider, useModules } from './core/modules/useModules';
import { buildModuleRoutes } from './core/modules/ModuleRouter';
import { FamilyProvider } from './modules/family/context/FamilyContext';

function AppContent() {
  const { t } = useTranslation();
  const { modules } = useModules();
  const [authState, setAuthState] = useState({
    isAuthenticated: isAuthenticated(),
    isLoading: isAuthLoading(),
  });
  const [version, setVersion] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    getVersion()
      .then((response) => {
        if (response.status === 200) {
          const { version: v, gitCommit } = response.data;
          setVersion(gitCommit ? `${v} (${gitCommit})` : v);
        }
      })
      .catch(() => {});

    const init = async () => {
      try {
        await checkAuthStatus();
      } catch (error) {
        console.error('Failed to check auth status:', error);
      }
    };
    init();

    const handleAuthChange = (event) => {
      setAuthState({
        isAuthenticated: event.detail.isAuthenticated,
        isLoading: event.detail.isLoading,
      });
    };

    window.addEventListener('auth-state-change', handleAuthChange);
    return () => window.removeEventListener('auth-state-change', handleAuthChange);
  }, []);

  useEffect(() => {
    if (authState.isAuthenticated && !authState.isLoading && !hasSeenOnboarding()) {
      setShowOnboarding(true);
    }
  }, [authState.isAuthenticated, authState.isLoading]);

  const footer = version ? <span>Version: {version}</span> : null;

  return (
    <Router>
      <AppLayout withSidebar={authState.isAuthenticated} footer={footer}>
        <ErrorBoundary t={t}>
          <Routes>
            <Route
              path="/login"
              element={
                authState.isLoading
                  ? null
                  : authState.isAuthenticated
                  ? <Navigate to="/dashboard" />
                  : <Login />
              }
            />
            <Route
              path="/signup"
              element={
                authState.isLoading
                  ? null
                  : authState.isAuthenticated
                  ? <Navigate to="/dashboard" />
                  : <Signup />
              }
            />

            <Route
              path="/"
              element={
                authState.isLoading
                  ? null
                  : authState.isAuthenticated
                  ? <Navigate to="/dashboard" />
                  : <LandingPage isAuthenticated={authState.isAuthenticated} />
              }
            />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/reports/:moduleId/:sectionKey" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/apikey" element={<PrivateRoute><ApiKey /></PrivateRoute>} />
            <Route path="/oauth2/redirect" element={<OAuth2Redirect />} />
            {/* Public on purpose: App Store Connect links here. */}
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {buildModuleRoutes(modules)}
          </Routes>
        </ErrorBoundary>
      </AppLayout>
      <OnboardingModal show={showOnboarding} onClose={() => setShowOnboarding(false)} />
    </Router>
  );
}

function App() {
  return (
    <TranslationProvider>
      <ToastProvider>
        <ModulesProvider>
          <FamilyProvider>
            <AppContent />
          </FamilyProvider>
        </ModulesProvider>
      </ToastProvider>
    </TranslationProvider>
  );
}

export default App;
